import { Fragment, FunctionComponent, ReactNode, useMemo, useRef } from 'react';
import cn from 'classnames';
import { DraftState, PrintArea, View } from '@/types';
import { isEmbroideryMode } from '@/utils/params';
import { usePinchProvider } from '@/hooks/usePinch';
import { getSortedConfigurationsOnView } from '@/model/draft/getSortedConfigurationsOnView';
import { getPrintAreaSize } from '@/model/printArea/getPrintAreaSize';
import { getPrintAreaForView } from '@/model/productType/getPrintAreaForView';
import { getViewMapByPrintArea } from '@/model/productType/getViewMapByPrintArea';
import { EmbroideryFrame } from './EmbroideryFrame';
import { MultiSelectProvider } from './MultiSelectContext';
import { MultiSelectFrame } from './MultiSelectFrame';
import { PrintAreaRenderer } from './PrintAreaRenderer';
import { TransformChangedCallback, TransformChangedProvider } from './TransformChangedContext';
import { Viewer } from './Viewer';
import { useSnapLinesProvider } from './hooks/useSnapLines';
import './DraftViewer.scss';

const PRINT_AREA_MARGIN_WITH_CONTROL = 64; // px 48px width view control + 16px width handle
const PRINT_AREA_MARGIN = 15; // px
const MENU_BASE_HEIGHT = 64; // px;
const getFocusedViewBox = (
  menuHeight: number | undefined,
  view: View,
  printArea: PrintArea | undefined,
  hasMultipleViews: boolean,
  focusPrintArea: boolean
) => {
  if (menuHeight && printArea && focusPrintArea) {
    // calculate a viewBox that is focused on the print area
    const { innerWidth, innerHeight } = window;
    const { width: printAreaWidth, height: printAreaHeight } = getPrintAreaSize(printArea);

    // calculate the margins from px to svg units
    const printAreaMarginHorizontal = hasMultipleViews
      ? PRINT_AREA_MARGIN_WITH_CONTROL
      : PRINT_AREA_MARGIN;
    const ratioHorizontalSvgToPx = Math.max(
      printAreaWidth / (innerWidth - 2 * printAreaMarginHorizontal),
      0
    );
    const marginHorizontal = ratioHorizontalSvgToPx * printAreaMarginHorizontal;

    const printAreaMarginBottom = Math.max(0, menuHeight - MENU_BASE_HEIGHT) + PRINT_AREA_MARGIN;
    const ratioVerticalSvgToPx = Math.max(
      printAreaHeight / (innerHeight - PRINT_AREA_MARGIN - printAreaMarginBottom),
      0
    );
    const marginTop = ratioVerticalSvgToPx * PRINT_AREA_MARGIN;
    const marginBottom = ratioVerticalSvgToPx * printAreaMarginBottom;

    const { x, y } = getViewMapByPrintArea(view, printArea).offset;
    return {
      x: x - marginHorizontal,
      y: y - marginTop,
      width: printAreaWidth + 2 * marginHorizontal,
      height: printAreaHeight + marginTop + marginBottom,
    };
  }
};

interface Props {
  children: ReactNode;
  draft: DraftState;
  menuHeight?: number;
  readonly?: boolean;
  onShowEdit?: () => void;
}

export const DraftViewer: FunctionComponent<Props> = ({
  draft,
  menuHeight,
  readonly = false,
  onShowEdit,
  children,
}) => {
  const {
    productType,
    appearance,
    view,
    configurations,
    configurationIds,
    validation,
    selectedConfigurationId,
  } = draft;
  const sortedConfigurations = useMemo(
    () => getSortedConfigurationsOnView(Object.values(configurations), configurationIds, view),
    [view, configurations, configurationIds]
  );

  const hasSelectedConfiguration = Boolean(
    selectedConfigurationId && configurations[selectedConfigurationId]
  );

  const SnapLinesProvider = useSnapLinesProvider();
  const [pinchRef, scale, origin, PinchProvider] = usePinchProvider(hasSelectedConfiguration);
  const transformCallbacks = useRef<Set<TransformChangedCallback>>(new Set());

  const printArea = getPrintAreaForView(productType, view, sortedConfigurations);

  const viewBox = getFocusedViewBox(
    menuHeight,
    view,
    printArea,
    productType.views.length > 1,
    hasSelectedConfiguration
  );

  return (
    <SnapLinesProvider>
      <PinchProvider>
        <TransformChangedProvider callbacks={transformCallbacks}>
          <MultiSelectProvider
            configurations={sortedConfigurations}
            selectedConfigurationId={selectedConfigurationId}
          >
            <main
              className={cn('react-viewer', {
                'react-viewer--views': productType.views.length > 1,
              })}
              ref={pinchRef}
            >
              <Viewer
                view={view}
                appearance={appearance}
                printArea={printArea}
                className="draft-viewer"
                configurations={sortedConfigurations}
                readOnly={readonly}
                scale={scale}
                origin={origin}
                errors={validation.errors}
                selectedConfigurationId={selectedConfigurationId}
                container={pinchRef}
                viewBox={viewBox}
                onShowEdit={onShowEdit}
              >
                {printArea && (
                  <Fragment>
                    <PrintAreaRenderer
                      key={printArea.id}
                      printArea={printArea}
                      isVisible={hasSelectedConfiguration}
                    />
                    {isEmbroideryMode() && (
                      <EmbroideryFrame
                        configurations={sortedConfigurations}
                        productType={productType}
                        printArea={printArea}
                        transformCallbacks={transformCallbacks}
                      />
                    )}
                    <MultiSelectFrame
                      configurations={sortedConfigurations}
                      transformCallbacks={transformCallbacks}
                    />
                  </Fragment>
                )}
              </Viewer>
              {children}
            </main>
          </MultiSelectProvider>
        </TransformChangedProvider>
      </PinchProvider>
    </SnapLinesProvider>
  );
};
